import { useAppSystem } from "@/global/hooks/useAppSystem";
import { EventType } from "@/global/store/data/types";
import { Divider } from "@/shared/base/components/Divider/Divider";
import {
  Box,
  Button,
  Grid,
  Stack,
  Theme,
  Typography,
  lighten,
} from "@mui/material";
import { PackageEvent } from "./PackageEvent";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

interface Props {
  title: string;
  description: string;
  events: EventType[];
  onClick?: (a: any) => void;
}

export function BuildYourOwnPackageCard(props: Props) {
  const { theme } = useAppSystem();
  const styles = useStyles(theme, props);
  const { sm: smDown } = useCssMediaQuery("down");

  return (
    <Stack sx={styles.root} onClick={props?.onClick}>
      <Box p={smDown ? 3 : 4}>
        <Typography
          variant="h6"
          fontWeight={700}
          fontSize={"1.2rem"}
          color="text.primary"
          lineHeight={1.2}
        >
          {props.title}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" mt={0.5}>
          {props.description}
        </Typography>
      </Box>

      <Divider>
        <Box
          px={smDown ? 3 : 4}
          sx={styles.buttonWrapper}
          className="PackageCard__buttonWrapper"
        >
          <Button
            variant="contained"
            size="medium"
            fullWidth
            sx={{ fontSize: "0.9rem" }}
          >
            Build Your Own
          </Button>
        </Box>
      </Divider>

      <Box p={smDown ? 3 : 4} pt={5}>
        <Grid container spacing={3}>
          {props.events.map((evt, idx) => {
            return (
              <Grid key={evt.eventId} item xs={12}>
                <PackageEvent
                  logoUrl={evt.logoUrl}
                  title={evt.title}
                  date={evt.date}
                  time={evt.time}
                  tag={evt.tag ? evt.tag : ""}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Stack>
  );
}

function useStyles(theme: Theme, states: Props) {
  // colors
  const primaryColor = theme.palette.primary.main;
  const dividerColor = theme.palette.divider;
  const hoverShadowColor = lighten(primaryColor, 0.8);
  const borderColor = lighten(primaryColor, 0.5);

  return {
    root: {
      width: "100%",
      height: "100%",
      backgroundColor: theme.palette.background.paper,
      borderRadius: "8px",
      outline: `2px solid ${borderColor}`,
      cursor: "pointer",
      transition: "all 0.2s ease",
      transformOrigin: "top top",
      boxShadow: `0 0 0 6px ${hoverShadowColor}`,

      "&:hover": {
        transform: `scale(1.04)`,

        "& .PackageCard__buttonWrapper": {
          opacity: 1,
        },

        [theme.breakpoints.down("md")]: {
          transform: `scale(1)`,
        },
      },
    },

    buttonWrapper: {
      opacity: 0,
      transition: "all 0.3s ease",

      [theme.breakpoints.down("md")]: {
        opacity: 1,
      },
    },
  };
}
